import { Avatar, colors } from "@mui/material";
import { GridApiCommon, GridCellModes, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { enqueueSnackbar } from "notistack";
import { createElement, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbCar, TbQuestionMark } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import MemoizedEditAutoComplete from "@/components/table/EditAutoCompletete";
import { stringToColor } from "@/helpers/strings";
import { useClinicUnassignedDriverAssignProcessRowUpdate } from "@/hooks/useClinicUnassignedDriverAssignProcessRowUpdate";
import { HotelClinicListItem } from "@/pages/HotelPlanning/clinic-arrival/types";
import useDriversContext from "@/providers/data/DriversContext";
import { useClinicUnassignedRowChanges } from "@/stores/use-clinic-arrival-unassigned-passengers-table";
import { DriverType } from "@/types/driver.types";

import ClinicUnassignedEditableColumn from "./ClinicUnassignedEditableColumn";

const errors: { cond: (row: HotelClinicListItem) => boolean; component: IconType; text: string; field: string }[] = [];

function useGetClinicUnassignedAssignationInfo({ row }: { row: HotelClinicListItem }) {
    const rowChanges = useClinicUnassignedRowChanges(row.uniqueIdentifier);
    const newRow = useMemo(() => ({ ...row, ...(rowChanges || {}) }), [row, rowChanges]);
    const error = useMemo(() => errors.find((item) => item.cond(newRow)), [newRow]);
    return { error, newRow };
}

export const ClinicUnassignedAssignationColumn = ({
    t,
    drivers,
}: {
    t: TFunction;
    drivers: DriverType[];
}): GridColDef<HotelClinicListItem> => {
    return {
        field: "userId",
        headerName: t("driver"),
        minWidth: 220,
        editable: true,
        flex: 0.5,
        pastedValueParser: (value: string, row) => {
            const err = errors.find((item) => item.cond(row));
            if (err) {
                enqueueSnackbar({ message: t(err.text), variant: "error" });
                return undefined;
            }
            const driver = drivers.find((item) => item.name === value || item.id === value || item.userId === value);
            return driver?.userId ?? undefined;
        },
        renderHeader: (params) => <TableHeader Component={TbCar} title={params.colDef.headerName!} />,
        valueGetter: (_val, row) => row.userId,
        renderCell: (params: GridRenderCellParams<HotelClinicListItem>) => (
            <AssignationMemoComponent
                api={params.api}
                row={params.row}
                field={params.field}
                value={params.value as string}
                gridRowId={String(params.id)}
            />
        ),
        renderEditCell: (params) => (
            <AssignationMemoEditComponent
                api={params.api}
                row={params.row}
                field={params.field}
                value={params.value as string}
                gridRowId={String(params.id)}
            />
        ),
    };
};

const AssignationMemoComponent = memo(
    ({
        value,
        row,
        field,
        api,
        gridRowId,
    }: {
        api: GridApiCommon;
        field: string;
        row: HotelClinicListItem;
        value: string;
        gridRowId: string;
    }) => {
        const { error } = useGetClinicUnassignedAssignationInfo({ row });

        const handleEdit = useCallback(() => {
            const mode = api.getCellMode(gridRowId, error?.field || field);
            if (mode !== GridCellModes.Edit) api.startCellEditMode({ field: error?.field || field, id: gridRowId });
        }, [api, field, error, gridRowId]);

        return (
            <ClinicUnassignedEditableColumn
                renderHistoryComp={(val) =>
                    val ? <ClinicUnassignedAssignationCell value={val as string} /> : <span>-</span>
                }
                handleEdit={handleEdit}
                api={api}
                field={field}
                id={gridRowId}
                editable
            >
                <ClinicUnassignedAssignationCell error={error} value={value} />
            </ClinicUnassignedEditableColumn>
        );
    }
);

export const ClinicUnassignedAssignationCell = memo(
    ({ value, error, className }: { error?: (typeof errors)[number]; className?: string; value: string | number }) => {
        const { t } = useTranslation();
        const { drivers } = useDriversContext();
        const driver = useMemo(() => drivers.find((item) => item.userId == value), [drivers, value]);

        const color = stringToColor(driver?.name || "", -15);
        const info = useMemo<{
            color: string;
            icon: IconType;
            text: string;
            hasUser?: boolean;
        }>(
            () =>
                error
                    ? {
                          color: colors.red[500],
                          icon: error.component,
                          text: error.text,
                      }
                    : driver
                      ? {
                            color: `#${color}`,
                            icon: driver.icon || TbCar,
                            hasUser: true,
                            text: driver.name,
                        }
                      : {
                            color: colors.grey[500],
                            icon: TbQuestionMark,
                            text: "buttons.assignDriver",
                        },
            [color, error, driver]
        );

        return (
            <div
                className={`flex items-center h-full gap-1 text-xs font-semibold ${className ?? ""}`}
                style={{ color: info.color }}
            >
                <Avatar sx={{ backgroundColor: info.color, color: "white", width: 25, height: 25 }} variant="rounded">
                    {createElement(info.icon)}
                </Avatar>
                {info.hasUser ? info.text : t(info.text)}
            </div>
        );
    }
);

const AssignationMemoEditComponent = memo(
    ({
        value,
        row,
        field,
        api,
        gridRowId,
    }: {
        api: GridApiCommon;
        field: string;
        row: HotelClinicListItem;
        value: string;
        gridRowId: string;
    }) => {
        const { error, newRow } = useGetClinicUnassignedAssignationInfo({ row });
        const { drivers } = useDriversContext();
        const processRowUpdateWithClipboard = useClinicUnassignedDriverAssignProcessRowUpdate();

        const val = newRow.userId ?? value;
        const options = useMemo(() => drivers.map((item) => ({ label: item.name, value: item.userId })), [drivers]);
        const driver = useMemo(() => drivers.find((item) => item.name === val || item.userId === val), [drivers, val]);

        const edit = error ? (
            <ClinicUnassignedAssignationCell className="!pl-4" error={error} value={value} />
        ) : (
            <MemoizedEditAutoComplete
                processRowUpdate={processRowUpdateWithClipboard}
                gridRowId={gridRowId}
                api={api}
                row={row}
                field={field}
                value={driver?.userId || ""}
                options={options}
                optionComp={UserOption}
                icon={TbCar}
                placeholder="driver"
                renderHistoryComp={(v) =>
                    v ? <ClinicUnassignedAssignationCell value={v as string} /> : <span>-</span>
                }
            />
        );
        return (
            <div
                data-clinic-arrival-no-row-dnd
                className="h-full min-h-0 w-full select-text"
                onPointerDown={(e) => e.stopPropagation()}
            >
                {edit}
            </div>
        );
    }
);

function UserOption({ option }: { option: { label: string; value: string | number } }) {
    return <ClinicUnassignedAssignationCell error={undefined} value={option.value} />;
}
